import { Base, property, ListModel, Action, ComputedUpdater } from "survey-core";
import { SurveyCreatorModel } from "../../creator-base";
import { ICreatorPlugin } from "../../creator-settings";
import { SurveyTextWorker, SurveyTextWorkerError } from "../../textWorker";
import { saveToFileHandler } from "../../utils/html-element-utils";
import { settings } from "../../creator-settings";
import { DomWindowHelper } from "survey-core";
import { CreatorDomHelper } from "../../dom-helper";

const maxErrorLength = 150;
export abstract class JsonEditorBaseModel extends Base {
  public isJSONChanged: boolean = false;
  private isApplying: boolean = false;
  public isProcessingImmediately: boolean = false;
  private static updateTextTimeout: number = 1000;
  private jsonEditorChangedTimeoutId: number = -1;
  @property() hasErrors: boolean;

  constructor(protected creator: SurveyCreatorModel) {
    super();
  }
  public get text(): string {
    return this.getText();
  }
  public set text(val: string) {
    this.setText(val);
  }
  protected abstract getText(): string;
  protected abstract setText(val: string): void;
  protected onEditorActivated(): void { }
  public onPluginActivate(): void {
    this.text = this.creator.text;
    this.onEditorActivated();
    this.isJSONChanged = false;
  }
  protected onTextChanged(): void {
    this.cancelScheduledProcessing();
    // setText has validated the text already, synchronously
    const needProcessErrors = !this.isProcessingImmediately;
    // with auto-save on, the pause after a keystroke applies the text the way a property change
    // in the designer does - otherwise the text waits for a read, a save or the tab switch
    if (!needProcessErrors && !this.creator.autoSaveEnabled) return;
    const self: JsonEditorBaseModel = this;
    const window = DomWindowHelper.getWindow();
    if (!window) return;
    this.jsonEditorChangedTimeoutId = window.setTimeout(() => {
      self.jsonEditorChangedTimeoutId = -1;
      if (needProcessErrors) {
        self.processErrors(self.text);
      }
      if (self.creator.autoSaveEnabled) {
        self.applyChanges();
      }
    }, JsonEditorBaseModel.updateTextTimeout);
  }
  private cancelScheduledProcessing(): void {
    if (this.jsonEditorChangedTimeoutId !== -1) {
      clearTimeout(this.jsonEditorChangedTimeoutId);
      this.jsonEditorChangedTimeoutId = -1;
    }
  }

  private errorListValue: ListModel;
  public get errorList(): ListModel {
    if (!this.errorListValue) {
      this.errorListValue = new ListModel({
        items: [],
        onSelectionChanged: (action: Action) => {
          const error: SurveyTextWorkerError = action.data.error;
          if (!!error)this.gotoError(error.at, error.rowAt, error.columnAt);
        },
        allowSelection: false,
        searchEnabled: false
      });
      this.errorListValue.cssClasses = {
        item: "svc-json-errors__item",
        itemBody: "svc-json-error",
        itemsContainer: "svc-json-errors"
      };
      this.errorListValue.hasVerticalScroller = true;
    }
    return this.errorListValue;
  }

  protected setErrors(errors: Array<SurveyTextWorkerError>): void {
    let hasErrors = errors.length > 0;
    if (hasErrors) {
      const actions = [];
      this.createErrorActions(errors).forEach(action => actions.push(action));
      this.errorList.setItems(actions);
    }
    this.hasErrors = hasErrors;
  }
  protected gotoError(at: number, row: number, column: number): void { }
  private createErrorActions(errors: Array<SurveyTextWorkerError>): Array<Action> {
    const res = [];
    let counter = 1;
    errors.forEach(error => {
      const line = error.rowAt > -1 ? "Line: " + (error.rowAt + 1) + ". " : "";
      let title = error.text;
      if (title.length > maxErrorLength + 3) {
        title = title.substring(0, maxErrorLength) + "...";
      }
      title = line + title;
      const at = error.at;
      res.push(new Action({
        id: "error_" + counter++,
        component: "json-error-item",
        title: title,
        tooltip: error.text,
        iconName: "icon-error",
        iconSize: "auto",
        data: {
          error: error,
          showFixButton: error.isFixable,
          fixError: () => {
            this.text = error.fixError(this.text);
          },
          fixButtonIcon: "icon-fix",
          //todo
          fixButtonTitle: "Fix error"
        }
      }));
    });
    return res;
  }
  public processErrors(text: string): void {
    const textWorker: SurveyTextWorker = this.createTextWorker();
    this.setErrors(textWorker.errors);
  }
  // true: nothing to apply, or applied. false: the text does not parse or has a blocking error,
  // and the survey is left as it is. Leaving the tab passes ignoreBlockingErrors: the host may
  // have allowed leaving with errors through onActiveTabChanging.
  public applyChanges(ignoreBlockingErrors: boolean = false): boolean {
    if (this.readOnly || !this.isJSONChanged) return true;
    // a handler of an event the apply itself raises may read creator.JSON, which applies this
    // very tab again: the apply already under way answers for it
    if (this.isApplying) return true;
    if (!ignoreBlockingErrors && this.allowingDeactivate() !== true) return false;
    this.isApplying = true;
    try {
      this.creator.selectedElement = undefined;
      this.creator.changeText(this.text, false, true);
      this.creator.selectedElement = this.creator.survey;
      // reset before setModified: with an auto-save delay of 0 the save runs synchronously, and
      // the save path applies the active tab again - the flag must already say there is nothing
      // left to apply
      this.isJSONChanged = false;
      this.creator.setModified({ type: "JSON_EDITOR" });
    } finally {
      this.isApplying = false;
    }
    return true;
  }
  public allowingDeactivate(): boolean {
    const textWorker: SurveyTextWorker = this.createTextWorker();
    if (!textWorker.isJsonCorrect) return undefined;
    return !textWorker.isJsonHasErrors;
  }
  private createTextWorker(): SurveyTextWorker {
    return new SurveyTextWorker(this.text, {
      validatePropertyValues: this.creator.validateJsonPropertyValues
    });
  }
  public dispose(): void {
    // the model is disposed as the tab closes: a pause it was waiting out has nothing to apply to
    this.cancelScheduledProcessing();
    super.dispose();
  }
  public get readOnly(): boolean {
    return this.creator.readOnly;
  }
}

export abstract class TabJsonEditorBasePlugin implements ICreatorPlugin {
  private importAction: Action;
  private exportAction: Action;
  private copyAction: Action;

  public static iconName = "icon-codeeditor-24x24";

  constructor(private creator: SurveyCreatorModel) {
    this.createActions().forEach(action => creator.toolbar.actions.push(action));
  }

  public saveToFileHandler = saveToFileHandler;

  public exportToFile(fileName: string) {
    if (this.model) {
      const jsonBlob = new Blob([this.model.text], { type: "application/json" });
      this.saveToFileHandler(fileName, jsonBlob);
    }
  }
  public importFromFile(file: File, callback?: (json: string) => void) {
    let fileReader = new FileReader();
    fileReader.onload = (e) => {
      const surveyJSONText = fileReader.result as string;
      if (this.model) {
        this.model.text = surveyJSONText;
        this.model.isJSONChanged = true;
      }
      callback && callback(surveyJSONText);
    };
    fileReader.readAsText(file);
  }
  public copyToClipboard() {
    if (this.model) {
      navigator.clipboard.writeText(this.model.text);
    }
  }

  protected createActions(): Array<Action> {
    const items: Array<Action> = [];

    this.importAction = new Action({
      id: "svc-json-import",
      iconName: "icon-load",
      iconSize: "auto",
      locTitleName: "ed.surveyJsonImportButton",
      locTooltipName: "ed.surveyJsonImportButton",
      visible: <any>new ComputedUpdater<boolean>(() => { return this.creator.activeTab === "json"; }),
      mode: "small",
      component: "sv-action-bar-item",
      needSeparator: true,
      action: () => {
        CreatorDomHelper.openFileDialog((file: File) => this.importFromFile(file));
      }
    });
    items.push(this.importAction);

    this.exportAction = new Action({
      id: "svc-json-export",
      iconName: "icon-download",
      iconSize: "auto",
      locTitleName: "ed.surveyJsonExportButton",
      locTooltipName: "ed.surveyJsonExportButton",
      visible: <any>new ComputedUpdater<boolean>(() => { return this.creator.activeTab === "json"; }),
      mode: "small",
      component: "sv-action-bar-item",
      action: () => {
        this.exportToFile(settings.jsonEditor.exportFileName);
      }
    });
    items.push(this.exportAction);

    this.copyAction = new Action({
      id: "svc-json-copy",
      iconName: "icon-copy",
      iconSize: "auto",
      locTitleName: "ed.surveyJsonCopyButton",
      locTooltipName: "ed.surveyJsonCopyButton",
      visible: <any>new ComputedUpdater<boolean>(() => { return this.creator.activeTab === "json"; }),
      mode: "small",
      component: "sv-action-bar-item",
      action: () => {
        this.copyToClipboard();
      }
    });
    items.push(this.copyAction);

    return items;
  }

  public model: JsonEditorBaseModel;
  public activate(): void {
    this.model = this.createModel(this.creator);
  }
  public deactivate(): boolean {
    if (this.model) {
      this.model.applyChanges(true);
      this.model.dispose();
      this.model = undefined;
    }
    return true;
  }
  public applyPendingChanges(): boolean {
    return !this.model || this.model.applyChanges();
  }
  public defaultAllowingDeactivate(): boolean {
    if (!this.model) return true;
    return this.model.allowingDeactivate();
  }
  protected abstract createModel(
    creator: SurveyCreatorModel
  ): JsonEditorBaseModel;
}
